/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import filesMap from '../../../../constants/MimeTypeConstants';
import { SORT_BY_CONSTANTS, SORT_ORDER_CONSTANTS } from '../../SearchConstants';
import GlobalsUtils from '../../../../utils/GlobalsUtils';
import SearchUtils from '../../../../utils/SearchUtils';
import SearchHostContext from '../../../../utils/SearchHostContext';
import WebStorage from '../../../../storage/WebStorage';
import isEqual from 'lodash.isequal';
const previousSearchQueryStorageKey = 'previousSearchQuery';
const yourWorkSortStateStorageKey = 'yourWorkSortState';
const yourWorkFilterStateStorageKey = 'yourWorkFilterState';
const yourWorkFilterOptionsStorageKey = 'yourWorkFilterOptions';
/**
 * Utils used by YourWork.
 */

export default class YourWorkUtils {
	/**
	 * Get file ext
	 *
	 * FIXME: this function appears to be not callable, since getFileTypeOfAsset() appears to be not callable
	 *
	 * @param {String} mimeType
	 */
	static getFileExt(mimeType: string) {
		let ext: any = 'app.type.unknown';

		if (GlobalsUtils.isString(mimeType) && mimeType.length > 0) {
			const dcxIndex = mimeType.lastIndexOf('dcxucf');
			ext = dcxIndex === -1 ? filesMap[mimeType] : filesMap[mimeType.substring(0, dcxIndex + 3)];

			if (!ext) {
				ext = 'app.type.unknown';
			} else {
				ext = ext.ext;
			}
		}

		return ext;
	}

	/**
	 * Takes in a data object for an Asset from a TableCell and determines the filetype.
	 * TODO: THE MIMETYPECONSTANTS.JS FILE IS MISSING A LOT OF FILE TYPES. Need to figure out what's missing and get translations of each (4/5/2019)
	 *
	 * FIXME: this function appears to be not callable
	 *
	 * @param {Object} data
	 */
	static getFileTypeOfAsset(data: Record<string, any>) {
		let fileTypeString = YourWorkUtils.getFileExt('');

		switch (data.assetCategory) {
			case 'ccFiles':
				fileTypeString = YourWorkUtils.getFileExt(data.image.attributes.assetType); // FIXME -- this might need to be `data.image.assetType` ???

				break;

			case 'lightroom': // TODO: In server, figure out how to get more specific type for Lightroom assets other than 'image'
			case 'cloudDocs':
			case 'libraries':
			case 'mobileCreations':
			case 'spark':
			case 'xdArtifacts':
				fileTypeString = YourWorkUtils.getFileExt(data.image.type); // FIXME, if we ever revive the use of getFileTypeOfAsset, then 'data.image.type' is incorrect for v1 data

				break;

			default:
				fileTypeString = YourWorkUtils.getFileExt('');
		}

		return fileTypeString;
	}

	/**
	 * Given the state.yourWorkFilterChips, userDidApplyFilters() determines if the user has applied filters or not.
	 * @param {Set<string>} yourWorkFilterChips
	 * @returns {bool}
	 */
	static userDidApplyFilters(yourWorkFilterChips: Set<string>): boolean {
		let didApplyFilters = false;

		if (yourWorkFilterChips && yourWorkFilterChips.size > 0) {
			didApplyFilters = true;
		}

		// TODO: In YourWorkFilterSideNav I also have this.props.setYourWorkFiltersApplied(true); in onClickApply(). I wonder if I can use that variable as well to know if a user applied filters, but if listening to it in redux store would cause an additional render of YourWork.js. It's a potential way to also know whether or not the user applied a filter.
		return didApplyFilters;
	}

	/**
	 * isSearchQueryWithZeroInitialResults determines on if, a BRAND NEW search query, whether that search query's INITIAL SET of results is zero. ie. we searched a search query that has absolutely zero assets from all sources.
	 * @param {boolean, any, Set<string>} didLoadResultsData, tableSource, yourWorkFilterChips
	 * @returns {bool}
	 */
	static isSearchQueryWithZeroInitialResults(
		didLoadResultsData: boolean,
		tableSource: any,
		yourWorkFilterChips: Set<string>,
	): boolean {
		const currentData = tableSource?.getCurrentData();
		return (
			didLoadResultsData &&
			tableSource &&
			Array.isArray(currentData) &&
			currentData.length === 0 &&
			!YourWorkUtils.userDidApplyFilters(yourWorkFilterChips)
		);
	}

	/**
	 * isSearchQueryWithFilterWithResults determines if, we do have a set of > 0 results for a given query -- POSSIBLY with filters or without. ie. an initial search query brought initial assets OR we had a search query w/ initial assets and we then filtered, and a call to the servers returns to us > 0 results for this query (and possible applied filters).
	 * @param {any, boolean} tableSource, showTabView
	 * @returns {bool}
	 */
	static isSearchQueryWithPossibleFilterWithResults(tableSource: any, showTabView: boolean) {
		return tableSource && tableSource.getCurrentData()?.length !== 0 && showTabView;
	}

	/**
	 * isSearchQueryWithFilterWithResults determines if, previously knowing that our current search query DOES HAVE an INITIAL SET of results, on a filter, we DO NOT HAVE ANY filtered results. ie. an initial search query brought initial assets, we then filtered, and a call to the servers returns to us 0 filtered results for this query and the applied filters.
	 * @param {any, boolean, boolean} tableSource, showTabView, didLoadResultsData
	 * @returns {bool}
	 */
	static isSearchQueryWithFilterWithZeroResults(tableSource: any, showTabView: boolean, didLoadResultsData: boolean) {
		return tableSource && tableSource.getCurrentData().length === 0 && showTabView && didLoadResultsData;
	}

	/**
	 * fetchPreviousSearchQuery fetches the previous search query
	 */
	static fetchPreviousSearchQuery() {
		let previousSearchQuery = '';
		const userId = SearchUtils.getUserIDForStorage();
		const previousSearchQueryFromStorage = WebStorage.sessionStorage.getItem(previousSearchQueryStorageKey);

		if (typeof previousSearchQueryFromStorage === 'object' && previousSearchQueryFromStorage !== null) {
			if (previousSearchQueryFromStorage[userId]) {
				previousSearchQuery = previousSearchQueryFromStorage[userId];
			}
		}

		return previousSearchQuery;
	}

	/**
	 * setPreviousSearchQuery sets the previous search query
	 * @param {string} searchQuery
	 */
	static setPreviousSearchQuery(searchQuery: string) {
		const userId = SearchUtils.getUserIDForStorage();
		let previousSearchQueryFromStorage: any = WebStorage.sessionStorage.getItem(previousSearchQueryStorageKey);

		if (searchQuery) {
			if (typeof previousSearchQueryFromStorage === 'object' && previousSearchQueryFromStorage !== null) {
				previousSearchQueryFromStorage[userId] = searchQuery;
			} else {
				previousSearchQueryFromStorage = {};
				previousSearchQueryFromStorage[userId] = searchQuery;
			}

			WebStorage.sessionStorage.setItem(previousSearchQueryStorageKey, previousSearchQueryFromStorage);
		}
	}

	/**
	 * fetchCurrentSortOrder fetches the current sort order for the Your Work view
	 * @returns {Object}
	 */
	static fetchCurrentSortOrder() {
		const userId = SearchUtils.getUserIDForStorage();
		let returnedSortState;
		const currentSort = {
			sortBy: SORT_BY_CONSTANTS.modificationDate,
			sortOrder: SORT_ORDER_CONSTANTS.descending,
		};
		const sortStateFromStorage: any = WebStorage.sessionStorage.getItem(yourWorkSortStateStorageKey);

		if (typeof sortStateFromStorage === 'object' && sortStateFromStorage !== null) {
			if (sortStateFromStorage[userId]) {
				returnedSortState = sortStateFromStorage[userId];
			} else {
				returnedSortState = currentSort;
				sortStateFromStorage[userId] = currentSort;
				WebStorage.sessionStorage.setItem(yourWorkSortStateStorageKey, sortStateFromStorage);
			}
		} else {
			returnedSortState = currentSort;
			YourWorkUtils.setDefaultSortOrder();
		}

		return returnedSortState;
	}

	/**
	 * setCurrentSortOrder sets the current sort order for the Your Work view
	 * @param {Object} data
	 */
	static setCurrentSortOrder(data: Sort) {
		const userId = SearchUtils.getUserIDForStorage();
		const filterSortOrderFromStorage: any = WebStorage.sessionStorage.getItem(yourWorkSortStateStorageKey);

		if (data) {
			if (typeof filterSortOrderFromStorage === 'object' && filterSortOrderFromStorage !== null) {
				filterSortOrderFromStorage[userId] = data;
				WebStorage.sessionStorage.setItem(yourWorkSortStateStorageKey, filterSortOrderFromStorage);
			} else {
				// Since we don't have previously stored sort order data, we store the default sort order
				YourWorkUtils.setDefaultSortOrder();
			}
		}
	}

	/**
	 * setDefaultSortOrder sets the default sort order for the Your Work view
	 */
	static setDefaultSortOrder() {
		const userId = SearchUtils.getUserIDForStorage();
		const defaultSort = {
			sortBy: SORT_BY_CONSTANTS.modificationDate,
			sortOrder: SORT_ORDER_CONSTANTS.descending,
		};
		const result = {};
		result[userId] = defaultSort;
		WebStorage.sessionStorage.setItem(yourWorkSortStateStorageKey, result);
	}

	/**
	 * fetchCurrentFilterOptions fetches the current filter options for the Your Work view
	 * @param {Object} searchConfig
	 * @returns {Array<Object>}
	 */
	static fetchCurrentFilterOptions(searchConfig: Record<string, any>): YourWorkFilters {
		const userId = SearchUtils.getUserIDForStorage();
		const filterOptionsFromStorage = WebStorage.sessionStorage.getItem(yourWorkFilterOptionsStorageKey);
		const filtersFromConfig = SearchUtils.getFilterOptions(searchConfig);

		let filtersFromStorage: YourWorkFilters;
		// checks whether filters are same or not structurally
		let areConfigAndStorageFiltersSame = true;

		if (
			typeof filterOptionsFromStorage === 'object' &&
			filterOptionsFromStorage !== null &&
			filterOptionsFromStorage[userId]
		) {
			filtersFromStorage = filterOptionsFromStorage[userId];

			if (Array.isArray(filtersFromStorage) && filtersFromStorage.length > 0) {
				for (const [index, section] of filtersFromStorage.entries()) {
					// check if options to be displayed are same or not
					// since the section will have array optionsToDisplay and config will have set
					// convert to array before checking
					if (!isEqual(section.optionsToDisplay, Array.from(filtersFromConfig[index].optionsToDisplay))) {
						areConfigAndStorageFiltersSame = false;
					}

					if (section.selectedOptions) {
						filtersFromStorage[index].selectedOptions = new Set(Array.from(section.selectedOptions));
					}
					if (section.selectedOption) {
						filtersFromStorage[index].selectedOption = section.selectedOption;
					}
				}
			}
		}
		return areConfigAndStorageFiltersSame ? filtersFromStorage : filtersFromConfig;
	}

	/**
	 * setCurrentFilterOptions sets the current filter options for the Your Work view
	 * @param {Array<Object>} data
	 */
	static setCurrentFilterOptions(data: Array<Record<string, any>>) {
		const userId = SearchUtils.getUserIDForStorage();
		let filterOptionsFromStorage: any = WebStorage.sessionStorage.getItem(yourWorkFilterOptionsStorageKey);

		if (data) {
			if (typeof filterOptionsFromStorage === 'object' && filterOptionsFromStorage !== null) {
				filterOptionsFromStorage[userId] = data;
			} else {
				const newFilterOptions = {};
				newFilterOptions[userId] = data;
				filterOptionsFromStorage = newFilterOptions;
			}

			WebStorage.sessionStorage.setItem(yourWorkFilterOptionsStorageKey, filterOptionsFromStorage);
		}
	}

	/**
	 * isDefaultFilterOptions checks if the default filter options are selected for the Your Work view
	 * @param {Array<Object>} yourWorkFiltersOptions
	 * @returns {boolean}
	 */
	static isDefaultFilterOptions(yourWorkFiltersOptions: Array<Record<string, any>>) {
		let isDefaultFilterOptions = false;
		const defaultFilterOptions = {
			dateModified: true,
			type: true,
			location: true,
		};

		if (Array.isArray(yourWorkFiltersOptions) && yourWorkFiltersOptions.length > 0) {
			for (const section of yourWorkFiltersOptions) {
				switch (section.label) {
					case 'dateModified':
						if (section.selectedOption !== 'all') {
							defaultFilterOptions.dateModified = false;
						}

						break;

					case 'type':
						if (Array.isArray(section.selectedOptions) && section.selectedOptions.length > 0) {
							defaultFilterOptions.type = false;
						}

						break;

					case 'location':
						if (Array.isArray(section.selectedOptions) && section.selectedOptions.length > 0) {
							defaultFilterOptions.location = false;
						}

						break;
				}
			}

			isDefaultFilterOptions = Object.values(defaultFilterOptions).every((section) => section);
		}

		return isDefaultFilterOptions;
	}

	/**
	 * fetchDefaultFilterState fetches the default filter state for the Your Work view for a given section
	 * @param {string} filterState
	 * @returns {boolean}
	 */
	static fetchDefaultFilterState(filterState: string) {
		const defaultFilterState = {
			yourWorkFiltersSelected: false,
			yourWorkFiltersApplied: false,
			yourWorkFiltersCleared: true,
		};
		return defaultFilterState[filterState];
	}

	/**
	 * fetchFilterState fetches the filter state for the Your Work view
	 * @param {string} filterStateValue
	 * @returns {boolean}
	 */
	static fetchFilterState(filterState: string) {
		const userId = SearchUtils.getUserIDForStorage();
		let returnedFilterState;
		const defaultFilterState = {
			yourWorkFiltersSelected: false,
			yourWorkFiltersApplied: false,
			yourWorkFiltersCleared: true,
		};
		const filterStateFromStorage = WebStorage.sessionStorage.getItem(yourWorkFilterStateStorageKey);

		if (typeof filterStateFromStorage === 'object' && filterStateFromStorage !== null) {
			if (filterStateFromStorage[userId] && typeof filterStateFromStorage[userId][filterState] === 'boolean') {
				returnedFilterState = filterStateFromStorage[userId][filterState];
			} else {
				returnedFilterState = defaultFilterState[filterState];
			}
		} else {
			returnedFilterState = defaultFilterState[filterState];
		}

		return returnedFilterState;
	}

	/**
	 * setFilterState sets the filter state for the Your Work view
	 * @param {string, boolean} filterState, filterStateValue
	 */
	static setFilterState(filterState: string, filterStateValue: boolean) {
		const userId = SearchUtils.getUserIDForStorage();
		const defaultFilterState = {
			yourWorkFiltersSelected: false,
			yourWorkFiltersApplied: false,
			yourWorkFiltersCleared: true,
		};
		let filterStateFromStorage: any = WebStorage.sessionStorage.getItem(yourWorkFilterStateStorageKey);

		if (typeof filterStateFromStorage === 'object' && filterStateFromStorage !== null) {
			if (filterStateFromStorage[userId]) {
				filterStateFromStorage[userId][filterState] = filterStateValue;
			} else {
				filterStateFromStorage[userId] = {};
				filterStateFromStorage[userId] = defaultFilterState;
				filterStateFromStorage[userId][filterState] = filterStateValue;
			}
		} else {
			filterStateFromStorage = {};
			filterStateFromStorage[userId] = defaultFilterState;
		}

		WebStorage.sessionStorage.setItem(yourWorkFilterStateStorageKey, filterStateFromStorage);
	}
}
